/**
 * MetricCard Component
 * Displays a single metric with optional trend indicator
 */

import React from 'react';
import { Card, Statistic, Tooltip } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
} from '@ant-design/icons';

type TrendDirection = 'up' | 'down' | 'neutral';
type TrendType = 'positive' | 'negative' | 'neutral';

interface MetricCardProps {
  title: string;
  value: number | string;
  suffix?: string;
  prefix?: React.ReactNode;
  precision?: number;
  trend?: {
    value: number;
    direction: TrendDirection;
    type?: TrendType; // Whether up is good or bad
  };
  loading?: boolean;
  tooltip?: string;
  icon?: React.ReactNode;
  color?: string;
  size?: 'small' | 'default' | 'large';
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  suffix,
  prefix,
  precision = 0,
  trend,
  loading = false,
  tooltip,
  icon,
  color,
  size = 'default',
}) => {
  // Determine trend color based on direction and type
  const getTrendColor = (): string => {
    if (!trend) return '#595959';

    const { direction, type = 'positive' } = trend;

    if (direction === 'neutral') return '#595959';

    // If type is positive, up is green and down is red
    // If type is negative, up is red and down is green
    if (type === 'positive') {
      return direction === 'up' ? '#52c41a' : '#f5222d';
    } else if (type === 'negative') {
      return direction === 'up' ? '#f5222d' : '#52c41a';
    }

    return '#595959';
  };

  const getTrendIcon = (): React.ReactNode => {
    if (!trend) return null;

    switch (trend.direction) {
      case 'up':
        return <ArrowUpOutlined />;
      case 'down':
        return <ArrowDownOutlined />;
      default:
        return <MinusOutlined />;
    }
  };

  const cardContent = (
    <Card
      loading={loading}
      size={size === 'small' ? 'small' : 'default'}
      style={{
        borderRadius: 8,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
      }}
      bodyStyle={{
        padding: size === 'small' ? 12 : size === 'large' ? 24 : 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {/* Icon */}
        {icon && (
          <div
            style={{
              fontSize: size === 'large' ? 32 : 24,
              color: color || '#1890ff',
              opacity: 0.85,
            }}
          >
            {icon}
          </div>
        )}

        {/* Statistic */}
        <div style={{ flex: 1 }}>
          <Statistic
            title={title}
            value={value}
            suffix={suffix}
            prefix={prefix}
            precision={typeof value === 'number' ? precision : undefined}
            valueStyle={{
              fontSize: size === 'large' ? 32 : size === 'small' ? 20 : 24,
              color: color || '#001529',
            }}
          />

          {/* Trend Indicator */}
          {trend && (
            <div
              style={{
                marginTop: 4,
                fontSize: 12,
                color: getTrendColor(),
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              {getTrendIcon()}
              <span>{trend.value}%</span>
              <span style={{ color: '#8c8c8c', marginLeft: 4 }}>
                vs last period
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{cardContent}</Tooltip>;
  }

  return cardContent;
};

export default MetricCard;
